import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Terminal, Loader2, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TCodeAnalysis = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/discovery/tcodes`);
      setData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return <Loader2 className="w-12 h-12 animate-spin mx-auto mt-20" />;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <Terminal className="w-6 h-6 mr-3 text-green-600" />
        Transaction Code Analysis
      </h3>
      
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-600 font-medium mb-1">Standard T-Codes</p>
          <p className="text-3xl font-bold text-blue-900">{data.total_standard}</p>
        </div>
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
          <p className="text-sm text-purple-600 font-medium mb-1">Custom T-Codes</p>
          <p className="text-3xl font-bold text-purple-900">{data.total_custom}</p>
        </div>
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-600 font-medium mb-1">Total Usage</p>
          <p className="text-3xl font-bold text-green-900">{data.total_usage.toLocaleString()}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-bold text-gray-900 mb-4">Standard SAP T-Codes</h4>
          <div className="space-y-2">
            {data.standard_tcodes.map((tc) => (
              <div key={tc.tcode} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-bold text-gray-900">{tc.tcode}</span>
                  <span className="text-sm text-gray-600">{tc.users} users</span>
                </div>
                <p className="text-sm text-gray-600">{tc.description}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">{tc.usage_count.toLocaleString()} executions</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-bold text-gray-900 mb-4">Custom Z T-Codes</h4>
          <div className="space-y-2">
            {data.custom_tcodes.map((tc) => (
              <div key={tc.tcode} className="border-2 border-purple-200 bg-purple-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-bold text-purple-900">{tc.tcode}</span>
                  <span className="text-sm text-purple-700">{tc.users} users</span>
                </div>
                <p className="text-sm text-gray-700">{tc.description}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-700">{tc.usage_count.toLocaleString()} executions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};